import React, { useMemo } from 'react';
import Lightbox, { ViewCallbackProps } from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

interface ReusableLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  image?: string;
  images?: string[];
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
}

interface ImageSlide {
  src: string;
}

const ReusableLightbox = ({
  isOpen,
  onClose,
  image,
  images,
  initialIndex = 0,
  onIndexChange,
}: ReusableLightboxProps) => {
  // Flexible to handle one or many slides.
  const slides: ImageSlide[] = useMemo((): ImageSlide[] => {
    if (images && images.length > 0) {
      return images.map((img: string): ImageSlide => ({ src: img }));
    } else if (image) {
      return [{ src: image }];
    }

    return [];
  }, [image, images]);

  if (!isOpen || slides.length === 0) {
    return null;
  }

  const isSingleImage: boolean = slides.length === 1;

  // For single images, we'll render a version without navigation.
  return (
    <Lightbox
      open={isOpen}
      close={onClose}
      slides={slides}
      index={initialIndex}
      plugins={[Zoom]}
      carousel={{
        // Make carousel finite when only one image (prevents looping behavior).
        finite: isSingleImage,
        // Don't preload adjacent images when only one.
        preload: isSingleImage ? 0 : 2,
      }}
      on={{
        view: onIndexChange
          ? ({ index }: ViewCallbackProps): void => onIndexChange(index)
          : undefined,
      }}
      render={{
        // Don't render navigation buttons for single image
        buttonPrev: isSingleImage ? () => null : undefined,
        buttonNext: isSingleImage ? () => null : undefined,
      }}
    />
  );
};

export default ReusableLightbox;
